import type { DbExecutor } from "../executor";
import type { CostEvidence, EstimateRequest, EvidenceProvider } from "../../estimating/evidence";

/**
 * MANUAL OVERRIDE — the user's own decisions, which always win.
 *
 * Two things it surfaces:
 *  1. a decision already recorded for THIS component (the line is priced
 *     because you said so, and the panel says that plainly);
 *  2. what you decided for the same kind of work on earlier jobs, so a
 *     considered number does not have to be re-derived every time.
 *
 * Nothing here is automatic: a past decision is a recommendation like any
 * other evidence, and applying it is still an explicit act.
 */
export class ManualOverrideProvider implements EvidenceProvider {
  readonly id = "manual-override";
  readonly label = "Your decision";
  readonly kind = "manual-override" as const;

  constructor(private readonly db: DbExecutor) {}

  async availability() {
    return { available: true, note: null };
  }

  async gather(request: EstimateRequest): Promise<CostEvidence[]> {
    const { subject } = request;
    if (subject.featureDefinitionId === null || subject.componentName === null) return [];

    const rows = await this.db.select<DecisionRow>(
      `SELECT d.id, d.chosen_unit_x100, d.unit, d.decided_at, d.market_zip, d.note,
              d.decision, p.job_number, p.id AS project_id, p.name AS project_name
         FROM estimate_evidence_decisions d
         JOIN projects p ON p.id = d.project_id
        WHERE d.feature_definition_id = ?
          AND d.component_name = ?
          AND d.unit = ?
          AND d.decision IN ('manual', 'alternative')
          AND d.project_id <> ?
        ORDER BY d.decided_at DESC
        LIMIT 5`,
      [subject.featureDefinitionId, subject.componentName, subject.unit, request.projectId ?? ""],
    );
    if (rows.length === 0) return [];

    const latest = rows[0];
    return [
      {
        id: `decision:${latest.id}`,
        providerId: this.id,
        providerLabel: "Your earlier decision",
        kind: this.kind,
        unitCostCentsX100: Number(latest.chosen_unit_x100),
        unit: latest.unit,
        lowCentsX100: Math.min(...rows.map((r) => Number(r.chosen_unit_x100))),
        highCentsX100: Math.max(...rows.map((r) => Number(r.chosen_unit_x100))),
        sampleSize: rows.length,
        asOf: latest.decided_at.slice(0, 10),
        market: latest.market_zip || null,
        explanation: [
          `You priced this yourself on job ${latest.job_number} (${latest.project_name}).`,
          latest.note ? `Your note: ${latest.note}` : "No note was recorded with that decision.",
          "Shown because you decided it — not because Berrier Reno worked it out.",
        ],
        observations: rows.map((row) => ({
          label: `Job ${row.job_number} — ${row.project_name}`,
          unitCostCentsX100: Number(row.chosen_unit_x100),
          quantityX100: null,
          date: row.decided_at.slice(0, 10),
          projectJobNumber: row.job_number,
          projectId: row.project_id,
          market: row.market_zip || null,
          ref: { kind: "decision", id: row.id },
        })),
        quality: "exact",
      },
    ];
  }
}

interface DecisionRow {
  id: string;
  chosen_unit_x100: number;
  unit: string;
  decided_at: string;
  market_zip: string;
  note: string;
  decision: string;
  job_number: string;
  project_id: string;
  project_name: string;
}
